import * as React from 'react';
import { useAsyncList } from '@react-stately/data';
import { useField } from 'formik';

import Combobox, { ComboboxProps } from '#/components/controls/Combobox';
import Field from '#/components/forms/Field';
import { isRequiredYupField, useYupRequiredFields } from '#/components/forms/Formik';

const ALL_COUNTRIES_API_URL = 'https://restcountries.com/v2/all?fields=name,alpha2Code';
const COUNTRIES_BY_NAME_API_URL = 'https://restcountries.com/v2/name';
const COUNTRIES_BY_CODE_API_URL = 'https://restcountries.com/v2/alpha';

const CountryCombobox: React.FC<CountryComboboxProps> = ({
  isLoading,
  onChange,
  onInputChange,
  value,
  ...props
}) => {
  const getKey = (item: CountryResponse) => item.alpha2Code;

  const list = useAsyncList<CountryResponse>({
    initialSelectedKeys: value ? [value] : undefined,
    getKey,
    async load({ signal, filterText }) {
      let apiUrl = ALL_COUNTRIES_API_URL;
      if (filterText) {
        apiUrl = `${COUNTRIES_BY_NAME_API_URL}/${filterText}`;
      } else if (value) {
        apiUrl = `${COUNTRIES_BY_CODE_API_URL}?codes=${value}`;
      }
      const res: CountryResponse[] = await fetch(apiUrl, { signal }).then((res) =>
        res.ok ? res.json() : [],
      );

      return { items: res };
    },
  });

  const handleChange = (value: string) => {
    list.setSelectedKeys(new Set([value]));
    onChange?.(value);
  };
  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    list.setFilterText(ev.target.value);
    onInputChange?.(ev);
  };

  return (
    <Combobox
      {...props}
      displayValue={(item) => item?.name}
      isLoading={isLoading ?? list.isLoading}
      items={list.items}
      getKey={getKey}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={value}
    >
      {(item) => (
        <Combobox.Option key={getKey(item)} value={item}>
          {item.name}
        </Combobox.Option>
      )}
    </Combobox>
  );
};
export type CountryComboboxProps = Omit<
  ComboboxProps,
  'children' | 'displayValue' | 'items' | 'getKey'
>;

export const CountryComboboxFormik: React.FC<CountryComboboxFormikProps> = ({
  invalid,
  label,
  name,
  ...props
}) => {
  const [field, meta] = useField(name);
  const requiredFields = useYupRequiredFields();
  const isRequired = isRequiredYupField(requiredFields, name);
  const showError = meta.touched && !!meta.error;

  return (
    <Field name={name}>
      {label && (
        <label className="uppercase mb-1 inline-block" htmlFor={`combobox-${name}`}>
          {label} {isRequired && <span className="text-red-600">*</span>}
        </label>
      )}
      <CountryCombobox
        {...field}
        size="lg"
        {...props}
        id={`combobox-${name}`}
        invalid={invalid ?? showError}
        onChange={field.onChange(field.name)}
        required={props.required ?? isRequired}
      />
    </Field>
  );
};
export type CountryComboboxFormikProps = CountryComboboxProps & {
  label?: string;
  name: string;
};

interface CountryResponse {
  alpha2Code: string;
  name: string;
}

export default CountryCombobox;
